import React, { useState, useMemo, useEffect } from 'react';
import { AT_EGO_STATES, AT_MARKERS } from '../../utils/textAnalysis';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';

const TransactionalAnalysisExperiment = ({ onBack, clients = [], preselectedClientId = '' }) => {
    const [view, setView] = useState(preselectedClientId ? 'experiment' : 'selection'); // selection, experiment, results
    const [selectedClientId, setSelectedClientId] = useState(preselectedClientId);
    const [answers, setAnswers] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [unassignedExperiments, setUnassignedExperiments] = useState([]);

    const activeClients = useMemo(() => (clients || []).filter(c => c.isActive !== false), [clients]);

    useEffect(() => {
        loadUnassigned();
    }, []);

    const loadUnassigned = async () => {
        try {
            const list = await DataManager.getAssessmentsByClient('unassigned');
            setUnassignedExperiments(list.filter(a => a.type === 'at_experiment'));
        } catch (err) {
            console.error("Error loading unassigned AT experiments:", err);
        }
    };

    const toggleMarker = (markerId) => {
        setAnswers(prev => {
            const next = { ...prev };
            if (next[markerId] === 'yes') {
                delete next[markerId];
            } else {
                next[markerId] = 'yes';
            }
            return next;
        });
    };

    const calculateResults = (customAnswers = null) => {
        const targetAnswers = customAnswers || answers;
        const baseIntensity = 10; // Minimum baseline for each state
        const scores = {
            parent_critical: baseIntensity,
            parent_nurturing: baseIntensity,
            adult: baseIntensity,
            child_free: baseIntensity,
            child_adapted: baseIntensity
        };

        Object.keys(targetAnswers).forEach(markerId => {
            if (targetAnswers[markerId] === 'yes') {
                for (const section of AT_MARKERS) {
                    const marker = section.markers.find(m => m.id === markerId);
                    if (marker) {
                        marker.states.forEach(stateId => {
                            scores[stateId] += 18; // Each marker adds to intensity
                        });
                        break;
                    }
                }
            }
        });

        // Normalize or cap at 100% (or let it be a relative intensity)
        const total = Object.values(scores).reduce((acc, v) => acc + v, 0);

        return AT_EGO_STATES.map(state => ({
            ...state,
            intensity: scores[state.id],
            percentage: Math.round((scores[state.id] / total) * 100)
        }));
    };

    const handleSaveResult = async () => {
        const targetClientId = selectedClientId || 'unassigned';
        setIsSaving(true);
        try {
            const results = calculateResults();
            await DataManager.saveAssessment(targetClientId, {
                type: 'at_experiment',
                answers,
                results: results.map(r => ({ id: r.id, name: r.name, intensity: r.intensity, percentage: r.percentage })),
                timestamp: new Date().toISOString()
            });
            setSaveSuccess(true);
            setTimeout(() => {
                setSaveSuccess(false);
                if (targetClientId === 'unassigned') {
                    loadUnassigned();
                    setView('selection');
                }
            }, 2000);
        } catch (err) {
            console.error("Error saving AT experiment:", err);
            alert("Errore durante il salvataggio.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAssignUnassigned = async (experimentId, clientId) => {
        if (!clientId) return;
        try {
            await DataManager.updateAssessment(experimentId, { clientId });
            loadUnassigned();
            alert("Esperimento assegnato con successo.");
        } catch (err) {
            console.error("Error assigning AT experiment:", err);
        }
    };

    const handleDeleteUnassigned = async (experimentId) => {
        if (!confirm("Eliminare questa analisi A.T.?")) return;
        try {
            await DataManager.deleteAssessment(experimentId);
            loadUnassigned();
        } catch (err) {
            console.error("Error deleting AT experiment:", err);
        }
    };

    // --- RENDER VIEWS ---

    if (view === 'selection') {
        return (
            <div className="max-w-4xl mx-auto p-10 bg-white rounded-[2.5rem] shadow-2xl animate-fade-in border border-slate-100">
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl shadow-inner shadow-indigo-200/50">✨</div>
                    <h2 className="text-4xl font-black text-slate-800 tracking-tight">Analisi Transazionale</h2>
                    <p className="text-slate-500 mt-3 font-medium text-lg">Mappatura degli Stati dell'Io ed Egogramma.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 flex flex-col justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                                Nuova Sessione
                            </h3>
                            <div className="mb-6">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Associa a Cliente</label>
                                <select
                                    className="w-full p-4 bg-white border-2 border-slate-100 rounded-2xl focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={selectedClientId}
                                    onChange={(e) => setSelectedClientId(e.target.value)}
                                >
                                    <option value="">-- Modalità Libera (Manuale) --</option>
                                    {activeClients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <Button
                            onClick={() => setView('experiment')}
                            className="py-5 bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100 text-lg"
                        >
                            Inizia Osservazione
                        </Button>
                    </div>

                    <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                                Analisi Pendenti
                            </h3>
                            <span className="bg-slate-200 text-slate-600 text-[10px] font-black px-2 py-1 rounded-full">{unassignedExperiments.length}</span>
                        </div>

                        <div className="flex-1 overflow-y-auto max-h-[180px] space-y-3 mb-6 pr-2 custom-scrollbar">
                            {unassignedExperiments.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-slate-400 text-sm italic py-10">
                                    Nessuna analisi da assegnare
                                </div>
                            ) : (
                                unassignedExperiments.map(exp => (
                                    <div key={exp.id} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:border-indigo-200 transition-all group">
                                        <div className="flex justify-between items-start mb-2">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{new Date(exp.timestamp).toLocaleDateString()}</p>
                                            <button onClick={() => handleDeleteUnassigned(exp.id)} className="text-slate-300 hover:text-red-500 transition-colors text-xs">🗑️</button>
                                        </div>
                                        <select
                                            className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-bold outline-none focus:border-indigo-500"
                                            onChange={(e) => handleAssignUnassigned(exp.id, e.target.value)}
                                            defaultValue=""
                                        >
                                            <option value="">Assegna a...</option>
                                            {activeClients.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))
                            )}
                        </div>
                        <button onClick={onBack} className="text-slate-400 font-bold hover:text-slate-600 py-2 transition-colors text-xs uppercase tracking-widest mt-auto">Dashboard</button>
                    </div>
                </div>
            </div>
        );
    }

    if (view === 'results') {
        const results = calculateResults();

        return (
            <div className="max-w-4xl mx-auto p-10 bg-white rounded-[2.5rem] shadow-2xl animate-fade-in border border-slate-100">
                <header className="mb-12 flex justify-between items-center bg-indigo-50/30 p-8 rounded-3xl border border-indigo-100/50">
                    <div>
                        <h2 className="text-3xl font-black text-indigo-900 tracking-tight">Egogramma Elaborato</h2>
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mt-1">
                            {selectedClientId ? `Cliente: ${activeClients.find(c => c.id === selectedClientId)?.name}` : 'Analisi Sperimentale'}
                        </p>
                    </div>
                    <div className="text-right">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter block">Data Analisi</span>
                        <p className="text-sm font-bold text-slate-700">{new Date().toLocaleDateString('it-IT')}</p>
                    </div>
                </header>

                {/* EGOGRAM VISUALIZATION */}
                <div className="mb-16">
                    <div className="flex items-end justify-around h-72 gap-4 px-4 bg-slate-50/50 rounded-[2rem] border border-slate-100 p-8 pb-12">
                        {results.map(res => (
                            <div key={res.id} className="flex-1 flex flex-col items-center group relative h-full">
                                {/* Percentage Label (Always Visible) */}
                                <div className="mb-2 text-[11px] font-black text-indigo-600 bg-white px-2 py-0.5 rounded-full border border-indigo-100 shadow-sm">
                                    {res.percentage}%
                                </div>

                                {/* Bar */}
                                <div className="w-full max-w-[50px] bg-slate-200 rounded-full flex-1 relative overflow-hidden flex items-end shadow-inner border border-white">
                                    <div
                                        className={`w-full transition-all duration-1000 ease-out rounded-full ${res.color === 'rose' ? 'bg-rose-500 shadow-lg shadow-rose-200' :
                                            res.color === 'emerald' ? 'bg-emerald-500 shadow-lg shadow-emerald-200' :
                                                res.color === 'slate' ? 'bg-slate-500 shadow-lg shadow-slate-200' :
                                                    res.color === 'amber' ? 'bg-amber-500 shadow-lg shadow-amber-200' :
                                                        'bg-indigo-600 shadow-lg shadow-indigo-200'
                                            }`}
                                        style={{ height: `${res.percentage * 2.5 + 5}%` }}
                                    />
                                    {/* Score tooltip on hover */}
                                    <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-black/5 flex items-center justify-center transition-opacity cursor-help">
                                        <span className="bg-slate-800 text-white text-[9px] font-black px-1.5 py-0.5 rounded">
                                            {res.intensity} pts
                                        </span>
                                    </div>
                                </div>

                                {/* Labels */}
                                <div className="mt-4 text-center">
                                    <span className="text-[14px] font-black text-slate-800 block mb-1">
                                        {res.id === 'parent_critical' ? 'GN' :
                                            res.id === 'parent_nurturing' ? 'GA' :
                                                res.id === 'adult' ? 'A' :
                                                    res.id === 'child_free' ? 'BL' : 'BA'}
                                    </span>
                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter max-w-[60px] block leading-none h-4">
                                        {res.name.split(' (')[0]}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-6">
                    <Button
                        onClick={handleSaveResult}
                        loading={isSaving}
                        className={`w-full py-5 rounded-[2rem] shadow-xl text-lg font-black transition-all ${saveSuccess ? 'bg-green-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                        {saveSuccess ? '✅ Egogramma Salvato' : '💾 Archivia Risultati'}
                    </Button>
                    <div className="grid grid-cols-2 gap-4">
                        <button onClick={() => { setAnswers({}); setView('experiment'); setSaveSuccess(false); }} className="py-4 bg-slate-50 text-slate-400 text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all">Ricomincia</button>
                        <button onClick={() => setView('selection')} className="py-4 bg-slate-50 text-slate-400 text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all">Nuovo</button>
                    </div>
                </div>
            </div>
        );
    }

    // --- EXPERIMENT VIEW ---
    return (
        <div className="max-w-6xl mx-auto p-10 bg-white rounded-[2.5rem] shadow-2xl animate-fade-in border border-slate-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-50/40 rounded-bl-[15rem] -z-0" />

            <div className="relative z-10">
                <header className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8 border-b border-slate-100 pb-8">
                    <div className="flex items-center gap-4">
                        <img src="/logo.png" alt="Logo" className="h-12 object-contain" />
                        <div>
                            <h2 className="text-4xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                                AwarenessTool <span className="text-slate-400 font-light">|</span> ✨ Stati dell'Io
                            </h2>
                            <p className="text-slate-500 font-medium mt-1 text-sm uppercase tracking-widest font-black opacity-60">Osservazione Fenomenologica A.T.</p>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <Button
                            variant="primary"
                            className="bg-indigo-600 px-10 py-4 rounded-2xl shadow-xl shadow-indigo-100 text-lg font-black"
                            onClick={() => setView('results')}
                        >
                            Vedi Egogramma ({Object.keys(answers).length})
                        </Button>
                        <button onClick={() => setView('selection')} className="px-6 text-slate-400 hover:text-slate-600 font-bold transition-colors uppercase text-xs tracking-widest">Annulla</button>
                    </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    {AT_MARKERS.map((section) => (
                        <div key={section.category} className="bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100/50 flex flex-col h-full hover:shadow-lg transition-all duration-300">
                            <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
                                {section.category}
                            </h3>
                            <div className="grid gap-2.5">
                                {section.markers.map(marker => {
                                    const isSelected = !!answers[marker.id];
                                    return (
                                        <button
                                            key={marker.id}
                                            onClick={() => toggleMarker(marker.id)}
                                            className={`w-full text-left p-4 rounded-2xl text-[10px] font-bold transition-all border-2 flex items-center justify-between group ${isSelected
                                                ? 'bg-indigo-600 text-white border-indigo-700 shadow-md transform scale-[1.02]'
                                                : 'bg-white text-slate-600 border-slate-50 hover:border-indigo-200 hover:bg-white'
                                                }`}
                                        >
                                            <span className="flex-1 leading-tight">{marker.label}</span>
                                            <div className={`w-4 h-4 min-w-[16px] rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-white border-white' : 'border-slate-100 group-hover:border-indigo-200'
                                                }`}>
                                                {isSelected && <span className="text-indigo-600 text-[10px] font-black">✓</span>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 pt-10 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6">
                    <p className="text-xs text-slate-400 font-bold italic bg-slate-50 px-6 py-3 rounded-full border border-slate-100">
                        🔍 {Object.keys(answers).length} indizi rilevati.
                    </p>
                    <Button
                        onClick={() => setView('results')}
                        className="bg-indigo-600 px-16 py-5 rounded-[2rem] shadow-2xl shadow-indigo-100 text-xl font-black active:scale-95 transition-all"
                    >
                        Genera Egogramma
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TransactionalAnalysisExperiment;
